import { appFetch } from '@/shared/api/app-client'
import type { ApiResponse } from '@/shared/api/app-types'

// ---------------------------------------------------------------------------
// 类型
// ---------------------------------------------------------------------------

export type CloneLanguage = 'zh' | 'en' | 'de' | 'it' | 'pt' | 'es' | 'ja' | 'ko' | 'fr' | 'ru'

export type ClonedVoice = {
  voiceId: string
  gmtCreate: string
  gmtModified: string
  status: string
  targetModel: string
}

export type CreateVoiceResult = {
  voiceId: string
  targetModel: string
}

export type ListVoicesResult = {
  voices: ClonedVoice[]
}

export const CLONE_LANGUAGE_OPTIONS: ReadonlyArray<{ value: CloneLanguage; label: string }> = [
  { value: 'zh', label: '中文' },
  { value: 'en', label: '英语' },
  { value: 'de', label: '德语' },
  { value: 'it', label: '意大利语' },
  { value: 'pt', label: '葡萄牙语' },
  { value: 'es', label: '西班牙语' },
  { value: 'ja', label: '日语' },
  { value: 'ko', label: '韩语' },
  { value: 'fr', label: '法语' },
  { value: 'ru', label: '俄语' },
] as const

// ---------------------------------------------------------------------------
// 端点解析
// ---------------------------------------------------------------------------

const viteEnv =
  typeof import.meta !== 'undefined' && typeof import.meta.env !== 'undefined'
    ? import.meta.env
    : ({} as Record<string, string | undefined>)

function resolveLocalMainSiteUrl() {
  if (typeof window === 'undefined') {
    return ''
  }
  const { hostname, protocol } = window.location
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:4010`
  }
  return window.location.origin
}

const apiBaseUrl = (viteEnv.VITE_AUTH_API_BASE_URL ?? viteEnv.VITE_MAIN_SITE_URL ?? resolveLocalMainSiteUrl()).replace(/\/+$/, '')

function endpoint(path: string) {
  return apiBaseUrl ? `${apiBaseUrl}${path}` : path
}

const CREATE_ENDPOINT = endpoint('/api/subsite/voice-clone/create')
const LIST_ENDPOINT = endpoint('/api/subsite/voice-clone/list')
const DELETE_ENDPOINT = endpoint('/api/subsite/voice-clone/delete')
const GENERATE_ENDPOINT = endpoint('/api/subsite/voice-clone/generate')

// ---------------------------------------------------------------------------
// 工具函数
// ---------------------------------------------------------------------------

/** 将 File 转为 base64 字符串（不含 Data URL 前缀） */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('音频文件读取失败。'))
        return
      }
      const base64 = result.includes(',') ? (result.split(',')[1] ?? result) : result
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('音频文件读取失败。'))
    reader.readAsDataURL(file)
  })
}

function formatDuration(seconds: number) {
  const total = Math.max(0, Math.round(seconds))
  const minutes = Math.floor(total / 60)
  const remain = total % 60
  return `${minutes}:${String(remain).padStart(2, '0')}`
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

class AliyunVoiceCloneService {
  /** 创建克隆音色 */
  async createVoice({
    audioFile,
    prefix,
    languageHints,
  }: {
    audioFile: File
    prefix: string
    languageHints: CloneLanguage
  }): Promise<CreateVoiceResult> {
    const audioBase64 = await fileToBase64(audioFile)
    const mimeType = audioFile.type.startsWith('audio/')
      ? (audioFile.type as 'audio/wav' | 'audio/mpeg' | 'audio/mp4')
      : 'audio/wav'

    const res = await appFetch<ApiResponse<CreateVoiceResult>>(CREATE_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({ audioBase64, mimeType, prefix, languageHints }),
    })
    return res.data
  }

  /** 查询已克隆音色列表 */
  async listVoices(): Promise<ListVoicesResult> {
    const res = await appFetch<ApiResponse<ListVoicesResult>>(LIST_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({ pageIndex: 0, pageSize: 50 }),
    })
    return res.data
  }

  /** 删除克隆音色 */
  async deleteVoice(voiceId: string): Promise<void> {
    await appFetch<ApiResponse<{ voiceId: string }>>(DELETE_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({ voiceId }),
    })
  }

  /** 用克隆音色试听生成语音（返回音频 Blob + 时长） */
  async generateSpeech({
    voiceId,
    text,
    onProgress,
  }: {
    voiceId: string
    text: string
    onProgress?: (stage: string) => void
  }): Promise<{ blob: Blob; objectUrl: string; duration: number }> {
    onProgress?.('正在用克隆音色生成语音...')

    const response = await fetch(GENERATE_ENDPOINT, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ voiceId, text }),
    })

    if (!response.ok) {
      let message = '试听生成失败。'
      try {
        const payload = (await response.json()) as { message?: string; error?: string }
        message = payload.message || payload.error || message
      } catch {
        // ignore
      }
      throw new Error(message)
    }

    onProgress?.('正在接收音频...')
    const blob = await response.blob()
    if (blob.size === 0) {
      throw new Error('试听生成没有返回音频数据。')
    }

    const rawType = blob.type.toLowerCase()
    const mimeType =
      rawType === 'audio/x-wav' || rawType === 'audio/wave'
        ? 'audio/wav'
        : rawType || 'audio/wav'
    const normalizedBlob = new Blob([blob], { type: mimeType })
    const objectUrl = URL.createObjectURL(normalizedBlob)

    const duration = await this.readDuration(normalizedBlob)
    return { blob: normalizedBlob, objectUrl, duration }
  }

  private async readDuration(blob: Blob): Promise<number> {
    if (typeof Audio === 'undefined') return 0
    const objectUrl = URL.createObjectURL(blob)
    try {
      const duration = await new Promise<number>((resolve) => {
        const audio = new Audio()
        audio.preload = 'metadata'
        audio.src = objectUrl
        audio.onloadedmetadata = () =>
          resolve(Number.isFinite(audio.duration) ? audio.duration : 0)
        audio.onerror = () => resolve(0)
      })
      return duration
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }
}

export const aliyunVoiceCloneService = new AliyunVoiceCloneService()
export { formatDuration }
